import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

  Alert,
  Button,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Search as SearchIcon } from '@mui/icons-material';
import { auth } from '../../../../../firebase';
import { SERVICE_ENDPOINTS } from '../../constants/serviceEndpoints';

const ServiceInputForm = ({ selectedService, inputs, onInputChange, disabled }) => {
  const [formData, setFormData] = useState({});
  const [parentInspectionData, setParentInspectionData] = useState(null);
  const [isInspecting, setIsInspecting] = useState(false);

  useEffect(() => {
    // Initialize form data based on service type
    const defaultData = getDefaultInputs(selectedService);
    setFormData(prevData => ({
      ...defaultData,
      ...prevData  // Keep any existing data but ensure defaults are set
    }));
    // No need to call onInputChange here since parent already tracks inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]); // Only depend on selectedService

  // Update formData when inputs change from parent
  useEffect(() => {
    if (inputs) {
      const defaultData = getDefaultInputs(selectedService);
      setFormData({
        ...defaultData,
        ...inputs  // Merge inputs with defaults to ensure no undefined objects
      });
    }
  }, [inputs, selectedService]);

  const getDefaultInputs = (selectedService) => {
    const defaults = {
      GEOCODING: {
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'US'
      },
      NCES: {
        name: '',
        state_abbr: '',
        ncessch: '',
        district_name: '',
        state_name: '',
        status: 'open',
        ownership: 'public',
        operational_model: 'traditional',
        address: {
          street: '',
          city: '',
          state: '',
          zip_code: ''
        },
        grades_offered: ['KG', '01', '02', '03', '04', '05']
      },
      ESRI: {
        latitude: '',
        longitude: ''
      },
      NEARBY_SCHOOLS: {
        latitude: '',
        longitude: '',
        radius_miles: '5',
        include_private: false,
        include_charter: true,
        school_types: ''
      },
      PROJECTIONS: {
        school_id: '',
        enrollment_data: {},
        projection_years: 5,
        method: 'linear'
      },
      METRICS: {
        district_id: '',
        school_ids: [],
        metric_types: ['enrollment', 'demographics', 'performance']
      },
      TEAM: {
        name: '',
        funder: '',
        school_uuids: []
      },
      SPLITS: {
        parent_ncessch: '',
        split_config: {
          elementary: {
            gradesServed: ['PK', 'KG', '01', '02', '03', '04', '05']
          },
          middle: {
            gradesServed: ['06', '07', '08']
          },
          high: {
            gradesServed: ['09', '10', '11', '12']
          }
        },
        addresses: {
          elementary: {
            address: '',
            city: '',
            state: '',
            zip: ''
          },
          middle: {
            address: '',
            city: '',
            state: '',
            zip: ''
          },
          high: {
            address: '',
            city: '',
            state: '',
            zip: ''
          }
        }
      }
    };
    return defaults[selectedService] || {};
  };

  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (onInputChange) {
      onInputChange(field, value);
    }
  };

  const handleInspectParent = async () => {
    if (!formData.parent_ncessch) {
      return;
    }

    setIsInspecting(true);
    try {
      // Use Firebase auth directly for consistency with working patterns
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Authentication required - please log in');
      }

      const token = await firebaseUser.getIdToken();
      const response = await fetch(`${SERVICE_ENDPOINTS.SPLITS}/${formData.parent_ncessch}/inspect`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to inspect parent school: ${response.status}`);
      }

      const inspectionData = await response.json();
      setParentInspectionData(inspectionData);
    } catch (error) {
      console.error('Error inspecting parent school:', error);
      setParentInspectionData({ error: error.message });
    } finally {
      setIsInspecting(false);
    }
  };


  const renderGeocodingInputs = () => {
    return (
      <Stack spacing={2}>
        <TextField
          key={`address-${formData.address}`}
          label="Street Address"
          value={formData.address || ''}
          onChange={(e) => handleInputChange('address', e.target.value)}
          placeholder="123 Main St"
          fullWidth
          disabled={disabled}
          required
        />
        <TextField
          key={`city-${formData.city}`}
          label="City"
          value={formData.city || ''}
          onChange={(e) => handleInputChange('city', e.target.value)}
          placeholder="New York"
          fullWidth
          disabled={disabled}
          required
        />
        <TextField
          key={`state-${formData.state}`}
          label="State"
          value={formData.state || ''}
          onChange={(e) => handleInputChange('state', e.target.value)}
          placeholder="NY"
          fullWidth
          disabled={disabled}
        />
        <TextField
          key={`zip-${formData.zip}`}
          label="ZIP Code"
          value={formData.zip || ''}
          onChange={(e) => handleInputChange('zip', e.target.value)}
          placeholder="10001"
          fullWidth
          disabled={disabled}
        />
        <TextField
          key={`country-${formData.country}`}
          label="Country"
          value={formData.country || 'US'}
          onChange={(e) => handleInputChange('country', e.target.value)}
          fullWidth
          disabled={disabled}
        />
    </Stack>
    );
  };

  const renderNCESInputs = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>Backend Service:</strong> Create or update school directory data without external Cloud Run calls
      </Alert>
      <TextField
        label="School Name"
        value={formData.name || ''}
        onChange={(e) => handleInputChange('name', e.target.value)}
        placeholder="Central High School"
        fullWidth
        disabled={disabled}
        required
        helperText="Official school name"
      />
      <TextField
        label="State Abbreviation"
        value={formData.state_abbr || ''}
        onChange={(e) => handleInputChange('state_abbr', e.target.value)}
        placeholder="IL"
        fullWidth
        disabled={disabled}
        required
        helperText="Two-letter state code"
      />
      <TextField
        label="NCESSCH (optional)"
        value={formData.ncessch || ''}
        onChange={(e) => handleInputChange('ncessch', e.target.value)}
        placeholder="360007702022"
        fullWidth
        disabled={disabled}
        helperText="12-digit NCES School ID"
      />
      <TextField
        label="District Name"
        value={formData.district_name || ''}
        onChange={(e) => handleInputChange('district_name', e.target.value)}
        placeholder="Springfield School District"
        fullWidth
        disabled={disabled}
      />
      <TextField
        label="State Name"
        value={formData.state_name || ''}
        onChange={(e) => handleInputChange('state_name', e.target.value)}
        placeholder="Illinois"
        fullWidth
        disabled={disabled}
      />
      <FormControl fullWidth>
        <InputLabel>School Status</InputLabel>
        <Select
          value={formData.status || 'open'}
          onChange={(e) => handleInputChange('status', e.target.value)}
          label="School Status"
          disabled={disabled}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Ownership</InputLabel>
        <Select
          value={formData.ownership || 'public'}
          onChange={(e) => handleInputChange('ownership', e.target.value)}
          label="Ownership"
          disabled={disabled}
        >
          <MenuItem value="public">Public</MenuItem>
          <MenuItem value="private">Private</MenuItem>
          <MenuItem value="federal">Federal</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Operational Model</InputLabel>
        <Select
          value={formData.operational_model || 'traditional'}
          onChange={(e) => handleInputChange('operational_model', e.target.value)}
          label="Operational Model"
          disabled={disabled}
        >
          <MenuItem value="traditional">Traditional</MenuItem>
          <MenuItem value="charter">Charter</MenuItem>
          <MenuItem value="magnet">Magnet</MenuItem>
          <MenuItem value="virtual">Virtual</MenuItem>
        </Select>
      </FormControl>
      <Divider>Address Information (Optional)</Divider>
      <TextField
        label="Street Address"
        value={formData.address?.street || ''}
        onChange={(e) => handleInputChange('address', { ...formData.address, street: e.target.value })}
        placeholder="123 School Street"
        fullWidth
        disabled={disabled}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="City"
          value={formData.address?.city || ''}
          onChange={(e) => handleInputChange('address', { ...formData.address, city: e.target.value })}
          placeholder="Springfield"
          fullWidth
          disabled={disabled}
        />
        <TextField
          label="State"
          value={formData.address?.state || ''}
          onChange={(e) => handleInputChange('address', { ...formData.address, state: e.target.value })}
          placeholder="IL"
          sx={{ width: '100px' }}
          disabled={disabled}
        />
        <TextField
          label="ZIP Code"
          value={formData.address?.zip_code || ''}
          onChange={(e) => handleInputChange('address', { ...formData.address, zip_code: e.target.value })}
          placeholder="62701"
          sx={{ width: '120px' }}
          disabled={disabled}
        />
      </Box>
      <TextField
        label="Grades Offered (comma-separated)"
        value={formData.grades_offered?.join(', ') || 'KG, 01, 02, 03, 04, 05'}
        onChange={(e) => {
          const grades = e.target.value.split(',').map(g => g.trim()).filter(g => g);
          handleInputChange('grades_offered', grades);
        }}
        placeholder="KG, 01, 02, 03, 04, 05"
        fullWidth
        disabled={disabled}
        helperText="Grade codes: KG, 01-12"
      />
    </Stack>
  );

  const renderESRIInputs = () => (
    <Stack spacing={2}>
      <TextField
        label="Latitude"
        type="number"
        value={formData.latitude || ''}
        onChange={(e) => handleInputChange('latitude', e.target.value)}
        placeholder="41.8781"
        fullWidth
          disabled={disabled}
        inputProps={{ step: 0.000001 }}
        helperText="Example: 41.8781 (Chicago)"
        required
      />
      <TextField
        label="Longitude"
        type="number"
        value={formData.longitude || ''}
        onChange={(e) => handleInputChange('longitude', e.target.value)}
        placeholder="-87.6298"
        fullWidth
          disabled={disabled}
        inputProps={{ step: 0.000001 }}
        helperText="Example: -87.6298 (Chicago)"
        required
      />
      <TextField
        label="Drive Times (comma-separated minutes)"
        value={formData.drive_times || '5,10,15,20,25,30'}
        onChange={(e) => handleInputChange('drive_times', e.target.value)}
        placeholder="5,10,15,20,25,30"
        fullWidth
          disabled={disabled}
        helperText="Valid values: 5, 10, 15, 20, 25, 30"
      />
      <TextField
        label="Travel Mode"
        value={formData.travel_mode || 'drive'}
        onChange={(e) => handleInputChange('travel_mode', e.target.value)}
        placeholder="drive"
        fullWidth
          disabled={disabled}
        helperText="'drive' (default) or 'walk'"
        select
        SelectProps={{ native: true }}
      >
        <option value="drive">drive</option>
        <option value="walk">walk</option>
      </TextField>
    </Stack>
  );

  const renderNearbySchoolsInputs = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>Test Mode:</strong> Calculate nearby schools without storing to database
      </Alert>
      <TextField
        label="School UUID"
        value={formData.school_uuid || ''}
        onChange={(e) => handleInputChange('school_uuid', e.target.value)}
        placeholder="school-uuid-123"
        fullWidth
          disabled={disabled}
        required
        helperText="UUID of the school to find nearby schools for"
      />
      <TextField
        label="Drive Times (comma-separated minutes)"
        value={formData.drive_times || '5,10,15'}
        onChange={(e) => handleInputChange('drive_times', e.target.value)}
        placeholder="5,10,15,20,25,30"
        fullWidth
          disabled={disabled}
        helperText="Drive time boundaries in minutes"
      />
      <TextField
        label="Data Years (comma-separated)"
        value={formData.data_years || '2019,2024'}
        onChange={(e) => handleInputChange('data_years', e.target.value)}
        placeholder="2019,2023"
        fullWidth
          disabled={disabled}
        helperText="Years to analyze for comparisons"
      />
    </Stack>
  );

  const renderProjectionsInputs = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>Test Mode:</strong> Calculate projections without storing to database
      </Alert>
      <TextField
        label="School UUID"
        value={formData.school_uuid || ''}
        onChange={(e) => handleInputChange('school_uuid', e.target.value)}
        placeholder="school-uuid-123"
        fullWidth
          disabled={disabled}
        required
        helperText="UUID of the school to generate projections for"
      />
      <TextField
        label="Projection Years"
        type="number"
        value={formData.projection_years || 5}
        onChange={(e) => handleInputChange('projection_years', e.target.value)}
        placeholder="5"
        fullWidth
          disabled={disabled}
        inputProps={{ min: 1, max: 20 }}
        helperText="Number of years to project forward"
      />
    </Stack>
  );

  const renderMetricsInputs = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>Test Mode:</strong> Calculate metrics without storing to database
      </Alert>
      <TextField
        label="School UUID"
        value={formData.school_uuid || ''}
        onChange={(e) => handleInputChange('school_uuid', e.target.value)}
        placeholder="school-uuid-123"
        fullWidth
          disabled={disabled}
        required
        helperText="UUID of the school to calculate metrics for"
      />
      <TextField
        label="Drive Times (comma-separated minutes)"
        value={formData.drive_times || '5,10,15'}
        onChange={(e) => handleInputChange('drive_times', e.target.value)}
        placeholder="5,10,15,20,25,30"
        fullWidth
          disabled={disabled}
        helperText="Drive time boundaries for analysis"
      />
      <TextField
        label="Data Years (comma-separated)"
        value={formData.data_years || '2019,2024'}
        onChange={(e) => handleInputChange('data_years', e.target.value)}
        placeholder="2019,2023"
        fullWidth
          disabled={disabled}
        helperText="Years to compare for trend analysis"
      />
    </Stack>
  );

  const renderTeamInputs = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>Backend Service:</strong> Create and manage teams and organizational assignments
      </Alert>
      <TextField
        label="Team Name"
        value={formData.name || ''}
        onChange={(e) => handleInputChange('name', e.target.value)}
        placeholder="Northeast Region Team"
        fullWidth
        disabled={disabled}
        required
        helperText="Unique team name for organizational structure"
      />
      <TextField
        label="Funder"
        value={formData.funder || ''}
        onChange={(e) => handleInputChange('funder', e.target.value)}
        placeholder="State Department of Education"
        fullWidth
        disabled={disabled}
        helperText="Organization or entity funding this team"
      />
      <TextField
        label="School UUIDs (comma-separated, optional)"
        value={formData.school_uuids?.join(', ') || ''}
        onChange={(e) => {
          const uuids = e.target.value.split(',').map(u => u.trim()).filter(u => u);
          handleInputChange('school_uuids', uuids);
        }}
        placeholder="uuid1, uuid2, uuid3"
        fullWidth
        disabled={disabled}
        multiline
        rows={3}
        helperText="Optional: School UUIDs to assign to this team upon creation"
      />
    </Stack>
  );

  const renderSplitsInputs = () => (
    <Stack spacing={3}>
      <Alert severity="info">
        <strong>Test Mode:</strong> Validate split configuration and preview results without saving to database
      </Alert>
      
      {/* Quick Test Data Buttons */}
      <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🚀 Quick Test Data
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() => {
                const abqTestData = {
                  parent_ncessch: '350012201071',
                  split_config: {
                    elementary: {
                      gradesServed: ['KG', '01', '02', '03', '04', '05']
                    },
                    middle: {
                      gradesServed: ['06', '07', '08']
                    },
                    high: {
                      gradesServed: ['09', '10', '11', '12']
                    }
                  },
                  addresses: {
                    elementary: {
                      address: '13201 LOMAS BLVD NE',
                      city: 'ALBUQUERQUE',
                      state: 'NM',
                      zip: '87112'
                    },
                    middle: {
                      address: '13301 LOMAS BLVD NE', 
                      city: 'ALBUQUERQUE',
                      state: 'NM',
                      zip: '87112'
                    },
                    high: {
                      address: '13401 LOMAS BLVD NE',
                      city: 'ALBUQUERQUE',
                      state: 'NM', 
                      zip: '87112'
                    }
                  }
                };
                setFormData(abqTestData);
                Object.keys(abqTestData).forEach(key => {
                  handleInputChange(key, abqTestData[key]);
                });
              }}
              disabled={disabled}
            >
              ABQ School of Excellence (K-12 → 3-way split)
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const simpleTestData = {
                  parent_ncessch: '350012201071',
                  split_config: {
                    elementary: {
                      gradesServed: ['KG', '01', '02', '03', '04', '05']
                    },
                    secondary: {
                      gradesServed: ['06', '07', '08', '09', '10', '11', '12']
                    }
                  },
                  addresses: {
                    elementary: {
                      address: '13201 LOMAS BLVD NE',
                      city: 'ALBUQUERQUE',
                      state: 'NM',
                      zip: '87112'
                    },
                    secondary: {
                      address: '13501 LOMAS BLVD NE',
                      city: 'ALBUQUERQUE', 
                      state: 'NM',
                      zip: '87112'
                    }
                  }
                };
                setFormData(simpleTestData);
                Object.keys(simpleTestData).forEach(key => {
                  handleInputChange(key, simpleTestData[key]);
                });
              }}
              disabled={disabled}
            >
              ABQ School (Elementary + Secondary split)
            </Button>
          </Stack>
        </CardContent>
      </Card>
      
      {/* Parent School Inspection Section */}
      <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔍 Parent School Inspection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Inspect parent school data before configuring splits
          </Typography>
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Parent School NCESSCH"
                value={formData.parent_ncessch || ''}
                onChange={(e) => {
                  handleInputChange('parent_ncessch', e.target.value);
                  // Clear previous inspection data when NCES ID changes
                  if (e.target.value !== formData.parent_ncessch) {
                    setParentInspectionData(null);
                  }
                }}
                placeholder="123456789012"
                disabled={disabled}
                helperText="12-digit NCES School ID of the school to split"
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={isInspecting ? <CircularProgress size={16} /> : <SearchIcon />}
                onClick={handleInspectParent}
                disabled={disabled || !formData.parent_ncessch || isInspecting}
                sx={{ height: 'fit-content' }}
              >
                {isInspecting ? 'Inspecting...' : 'Inspect Parent'}
              </Button>
            </Box>

            {/* Inspection Results */}
            {parentInspectionData && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  {parentInspectionData.error ? (
                    <Alert severity="error">
                      <strong>Inspection Failed:</strong> {parentInspectionData.error}
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        📊 Parent School Data Summary
                      </Typography>
                      
                      {/* Basic School Info */}
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle2">🏫 Basic Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              <strong>School Name:</strong> {parentInspectionData.parent_school?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>District:</strong> {parentInspectionData.parent_school?.current_directory?.lea_name || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>State:</strong> {parentInspectionData.parent_school?.current_directory?.state_abbr || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Grades Offered:</strong> {parentInspectionData.enrollment_analysis?.grades_with_data?.join(', ') || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Current Address:</strong> {
                                parentInspectionData.current_location 
                                  ? `${parentInspectionData.current_location.address}, ${parentInspectionData.current_location.city}, ${parentInspectionData.current_location.state} ${parentInspectionData.current_location.zip_code}` 
                                  : 'N/A'
                              }
                            </Typography>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>

                      {/* Enrollment Data */}
                      {parentInspectionData.enrollment_analysis && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">📈 Enrollment Summary</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              <Typography variant="body2">
                                <strong>Total Students (2023):</strong> {parentInspectionData.enrollment_analysis.total_enrollment_2023 || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Grades with Data:</strong> {parentInspectionData.enrollment_analysis.grades_with_data?.join(', ') || 'N/A'}
                              </Typography>
                              {parentInspectionData.enrollment_analysis.enrollment_by_grade && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Enrollment by Grade (2023):
                                  </Typography>
                                  <Box sx={{ ml: 2, maxHeight: 200, overflow: 'auto' }}>
                                    {Object.entries(parentInspectionData.enrollment_analysis.enrollment_by_grade).map(([grade, data]) => (
                                      <Typography key={grade} variant="body2">
                                        Grade {grade}: {data.total} students
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Team Assignments */}
                      {parentInspectionData.team_assignments && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">👥 Team Assignments</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              <Typography variant="body2">
                                <strong>Assigned Teams:</strong> {parentInspectionData.team_assignments.length || 0}
                              </Typography>
                              {parentInspectionData.team_assignments.map((team, index) => (
                                <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                                  • {team.name} ({team.funder || 'No funder specified'})
                                </Typography>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {/* Split Recommendations */}
                      {parentInspectionData.split_recommendations && (
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">💡 Split Recommendations</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              Based on the parent school's grade distribution, here are recommended split configurations:
                            </Alert>
                            {parentInspectionData.split_recommendations.map((recommendation, index) => (
                              <Box key={index} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {recommendation.type} Split
                                </Typography>
                                <Stack spacing={1}>
                                  {Object.entries(recommendation.configuration).map(([level, config]) => (
                                    <Typography key={level} variant="body2">
                                      <strong>{level}:</strong> Grades {config.grades.join(', ')} ({config.estimated_students} est. students)
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        </CardContent>
      </Card>
      
      {/* Original Split Configuration Form */}
      <Divider>
        <Typography variant="body2" color="text.secondary">
          Split Configuration
        </Typography>
      </Divider>
      
      {/* Elementary School */}
      <Box>
        <Typography variant="h6" gutterBottom>Elementary School</Typography>
        <Stack spacing={2}>
          <TextField
            label="Grades Served (comma-separated)"
            value={formData.split_config?.elementary?.gradesServed?.join(', ') || 'PK, KG, 01, 02, 03, 04, 05'}
            onChange={(e) => {
              const grades = e.target.value.split(',').map(g => g.trim()).filter(g => g);
              handleInputChange('split_config', {
                ...(formData.split_config || {}),
                elementary: { 
                  ...(formData.split_config?.elementary || {}), 
                  gradesServed: grades 
                }
              });
            }}
            placeholder="PK, KG, 01, 02, 03, 04, 05"
            fullWidth
            disabled={disabled}
          />
          <TextField
            label="Elementary School Address"
            value={formData.addresses?.elementary?.address || ''}
            onChange={(e) => {
              handleInputChange('addresses', {
                ...(formData.addresses || {}),
                elementary: { 
                  ...(formData.addresses?.elementary || {}), 
                  address: e.target.value 
                }
              });
            }}
            placeholder="123 Elementary St"
            fullWidth
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              value={formData.addresses?.elementary?.city || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  elementary: { 
                    ...(formData.addresses?.elementary || {}), 
                    city: e.target.value 
                  }
                });
              }}
              placeholder="Springfield"
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="State"
              value={formData.addresses?.elementary?.state || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  elementary: { 
                    ...(formData.addresses?.elementary || {}), 
                    state: e.target.value 
                  }
                });
              }}
              placeholder="IL"
              sx={{ width: '100px' }}
              disabled={disabled}
            />
            <TextField
              label="ZIP"
              value={formData.addresses?.elementary?.zip || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  elementary: { 
                    ...(formData.addresses?.elementary || {}), 
                    zip: e.target.value 
                  }
                });
              }}
              placeholder="62701"
              sx={{ width: '120px' }}
              disabled={disabled}
            />
          </Box>
        </Stack>
      </Box>
      
      {/* Middle School */}
      <Box>
        <Typography variant="h6" gutterBottom>Middle School</Typography>
        <Stack spacing={2}>
          <TextField
            label="Grades Served (comma-separated)"
            value={formData.split_config?.middle?.gradesServed?.join(', ') || '06, 07, 08'}
            onChange={(e) => {
              const grades = e.target.value.split(',').map(g => g.trim()).filter(g => g);
              handleInputChange('split_config', {
                ...(formData.split_config || {}),
                middle: { 
                  ...(formData.split_config?.middle || {}), 
                  gradesServed: grades 
                }
              });
            }}
            placeholder="06, 07, 08"
            fullWidth
            disabled={disabled}
          />
          <TextField
            label="Middle School Address"
            value={formData.addresses?.middle?.address || ''}
            onChange={(e) => {
              handleInputChange('addresses', {
                ...(formData.addresses || {}),
                middle: { 
                  ...(formData.addresses?.middle || {}), 
                  address: e.target.value 
                }
              });
            }}
            placeholder="456 Middle Ave"
            fullWidth
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              value={formData.addresses?.middle?.city || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  middle: { 
                    ...(formData.addresses?.middle || {}), 
                    city: e.target.value 
                  }
                });
              }}
              placeholder="Springfield"
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="State"
              value={formData.addresses?.middle?.state || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  middle: { 
                    ...(formData.addresses?.middle || {}), 
                    state: e.target.value 
                  }
                });
              }}
              placeholder="IL"
              sx={{ width: '100px' }}
              disabled={disabled}
            />
            <TextField
              label="ZIP"
              value={formData.addresses?.middle?.zip || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  middle: { 
                    ...(formData.addresses?.middle || {}), 
                    zip: e.target.value 
                  }
                });
              }}
              placeholder="62701"
              sx={{ width: '120px' }}
              disabled={disabled}
            />
          </Box>
        </Stack>
      </Box>
      
      {/* High School */}
      <Box>
        <Typography variant="h6" gutterBottom>High School</Typography>
        <Stack spacing={2}>
          <TextField
            label="Grades Served (comma-separated)"
            value={formData.split_config?.high?.gradesServed?.join(', ') || '09, 10, 11, 12'}
            onChange={(e) => {
              const grades = e.target.value.split(',').map(g => g.trim()).filter(g => g);
              handleInputChange('split_config', {
                ...(formData.split_config || {}),
                high: { 
                  ...(formData.split_config?.high || {}), 
                  gradesServed: grades 
                }
              });
            }}
            placeholder="09, 10, 11, 12"
            fullWidth
            disabled={disabled}
          />
          <TextField
            label="High School Address"
            value={formData.addresses?.high?.address || ''}
            onChange={(e) => {
              handleInputChange('addresses', {
                ...(formData.addresses || {}),
                high: { 
                  ...(formData.addresses?.high || {}), 
                  address: e.target.value 
                }
              });
            }}
            placeholder="789 High School Blvd"
            fullWidth
            disabled={disabled}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              value={formData.addresses?.high?.city || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  high: { 
                    ...(formData.addresses?.high || {}), 
                    city: e.target.value 
                  }
                });
              }}
              placeholder="Springfield"
              fullWidth
              disabled={disabled}
            />
            <TextField
              label="State"
              value={formData.addresses?.high?.state || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  high: { 
                    ...(formData.addresses?.high || {}), 
                    state: e.target.value 
                  }
                });
              }}
              placeholder="IL"
              sx={{ width: '100px' }}
              disabled={disabled}
            />
            <TextField
              label="ZIP"
              value={formData.addresses?.high?.zip || ''}
              onChange={(e) => {
                handleInputChange('addresses', {
                  ...(formData.addresses || {}),
                  high: { 
                    ...(formData.addresses?.high || {}), 
                    zip: e.target.value 
                  }
                });
              }}
              placeholder="62701"
              sx={{ width: '120px' }}
              disabled={disabled}
            />
          </Box>
        </Stack>
      </Box>
    </Stack>
  );

  const renderInputs = () => {
    switch (selectedService) {
      case 'GEOCODING':
        return renderGeocodingInputs();
      case 'NCES':
        return renderNCESInputs();
      case 'ESRI':
        return renderESRIInputs();
      case 'NEARBY_SCHOOLS':
        return renderNearbySchoolsInputs();
      case 'PROJECTIONS':
        return renderProjectionsInputs();
      case 'METRICS':
        return renderMetricsInputs();
      case 'TEAM':
        return renderTeamInputs();
      case 'SPLITS':
        return renderSplitsInputs();
      default:
        return (
          <Alert severity="warning">
            Service "{selectedService}" not yet configured for testing
          </Alert>
        );
    }
  };

  return (
    <Box>
      {renderInputs()}
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Fill in the required fields for the {selectedService} service
      </Typography>
      
      {selectedService === 'GEOCODING' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            💡 Example Addresses to Test:
          </Typography>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  id: 'google_hq',
                  address: '1600 Amphitheatre Parkway',
                  city: 'Mountain View',
                  state: 'CA',
                  zip: '94043',
                  country: 'US'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🏢 Google HQ - Mountain View, CA
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  id: 'navy_pier',
                  address: '600 E Grand Ave',
                  city: 'Chicago',
                  state: 'IL',
                  zip: '60611',
                  country: 'US'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🎡 Navy Pier - Chicago, IL
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  id: 'golden_gate',
                  address: 'Golden Gate Bridge',
                  city: 'San Francisco',
                  state: 'CA',
                  zip: '',
                  country: 'US'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🌉 Golden Gate Bridge - San Francisco, CA
            </Button>
          </Stack>
        </Box>
      )}

      {selectedService === 'ESRI' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            📍 Example Coordinates to Test:
          </Typography>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  latitude: '41.8781',
                  longitude: '-87.6298'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🏙️ Chicago, IL - Downtown
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  latitude: '40.7128',
                  longitude: '-74.0060'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🗽 New York City, NY - Manhattan
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  latitude: '34.0522',
                  longitude: '-118.2437'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🌴 Los Angeles, CA - Downtown
            </Button>
          </Stack>
        </Box>
      )}

      {selectedService === 'METRICS' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            🏫 Example School UUIDs to Test:
          </Typography>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: 'e4982f53-fa1e-49a4-84d2-87dea5831697',
                  drive_times: '5,10,15,20,25,30',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              📊 KIPP TRUTH EL - Texas (Full Data Available)
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '29f425b1-7f92-4c60-9ee1-240cca285100',
                  drive_times: '5,10,15,20,25,30',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              📈 School Sample 2 - Standard Analysis
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '46896654-2793-4d0c-8c40-2552843dc0d3',
                  drive_times: '5,10,15,20,25,30',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🎯 School Sample 3 - Complete Metrics
            </Button>
          </Stack>
        </Box>
      )}

      {selectedService === 'NEARBY_SCHOOLS' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            🏫 Example School UUIDs to Test:
          </Typography>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: 'e4982f53-fa1e-49a4-84d2-87dea5831697',
                  drive_times: '5,10,15,20,25,30',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🗺️ KIPP TRUTH EL - Texas (Urban Location)
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '29f425b1-7f92-4c60-9ee1-240cca285100',
                  drive_times: '5,10,15,20,25,30',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🌆 School Sample 2 - Find Nearby Schools
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '46896654-2793-4d0c-8c40-2552843dc0d3',
                  drive_times: '5,10',
                  data_years: '2019,2024'
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🏘️ School Sample 3 - Competitive Analysis
            </Button>
          </Stack>
        </Box>
      )}

      {selectedService === 'PROJECTIONS' && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            🏫 Example School UUIDs to Test:
          </Typography>
          <Stack spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: 'e4982f53-fa1e-49a4-84d2-87dea5831697',
                  projection_years: 5
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              📈 KIPP TRUTH EL - 5 Year Projections
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '29f425b1-7f92-4c60-9ee1-240cca285100',
                  projection_years: 10
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              📊 School Sample 2 - 10 Year Forecast
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const exampleData = {
                  school_uuid: '46896654-2793-4d0c-8c40-2552843dc0d3',
                  projection_years: 3
                };
                const newData = { ...formData, ...exampleData };
                setFormData(newData);
                if (onInputChange) {
                  Object.entries(exampleData).forEach(([key, value]) => {
                    onInputChange(key, value);
                  });
                }
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              🎯 School Sample 3 - 3 Year Analysis
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ServiceInputForm; 